/**
 * useKeyboardShortcuts (P2.1-S5) — register page-local shortcuts.
 *
 * Format key:
 *   "cmd+s" → Cmd (Mac) hoặc Ctrl (Win/Linux) + S
 *   "del"   → Delete hoặc Backspace
 *   "cmd+d" → Cmd/Ctrl + D
 *
 * Skip nếu user đang gõ trong INPUT/TEXTAREA/SELECT/contenteditable
 * (trừ Cmd+S vẫn work — save phải chạy được khi đang gõ form).
 *
 * Doc 33 §6.
 */
import * as React from "react";

type Handler = (e: KeyboardEvent) => void;
type ShortcutMap = Record<string, Handler>;

interface Options {
  /** Bật/tắt toàn bộ shortcuts (vd disable khi đang loading). */
  enabled?: boolean;
}

const ALWAYS_ALLOW_IN_INPUT = new Set(["cmd+s"]);

function normalizeEvent(e: KeyboardEvent): string | null {
  const parts: string[] = [];
  if (e.metaKey || e.ctrlKey) parts.push("cmd");
  if (e.shiftKey) parts.push("shift");
  if (e.altKey) parts.push("alt");
  const key = e.key.toLowerCase();

  // Special keys
  if (key === "delete" || key === "backspace") {
    parts.push("del");
  } else if (key.length === 1) {
    parts.push(key);
  } else {
    return null;
  }

  return parts.join("+");
}

function isInputFocused(): boolean {
  const el = document.activeElement;
  if (!el) return false;
  const tag = el.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  if ((el as HTMLElement).isContentEditable) return true;
  return false;
}

export function useKeyboardShortcuts(
  shortcuts: ShortcutMap,
  options: Options = {},
): void {
  const { enabled = true } = options;

  // Keep latest map ref nên không phải re-bind handler mỗi render
  const mapRef = React.useRef(shortcuts);
  mapRef.current = shortcuts;

  React.useEffect(() => {
    if (!enabled) return;

    const handler = (e: KeyboardEvent): void => {
      const combo = normalizeEvent(e);
      if (!combo) return;
      const fn = mapRef.current[combo];
      if (!fn) return;
      // Skip when typing trong input field, trừ một số shortcut quan trọng
      if (isInputFocused() && !ALWAYS_ALLOW_IN_INPUT.has(combo)) return;
      e.preventDefault();
      fn(e);
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [enabled]);
}
